// AuthModal.jsx - Authentication Modal Component
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const AuthModal = ({ onClose, onForgotPassword }) => {
  const { signUp, signIn, signInWithGoogle, signInWithFacebook } = useAuth();
  
  const [authTab, setAuthTab] = useState('signin');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');

  const handleEmailAuth = async () => {
    setAuthError('');
    
    if (!authEmail || !authPassword) {
      setAuthError('Please enter both email and password');
      return;
    }
    
    if (authPassword.length < 6) {
      setAuthError('Password must be at least 6 characters');
      return;
    }
    
    try {
      if (authTab === 'signup') {
        await signUp(authEmail, authPassword);
        alert('✅ Account created successfully!\n\nPlease check your email to verify your account.');
      } else {
        await signIn(authEmail, authPassword);
        alert('✅ Signed in successfully!');
      }
      onClose();
    } catch (error) {
      let errorMessage = '';
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = '❌ This email is already registered!\n\nPlease sign in instead, or use a different email.';
          setAuthTab('signin');
          break;
        case 'auth/invalid-email':
          errorMessage = '❌ Invalid email address format';
          break;
        case 'auth/weak-password':
          errorMessage = '❌ Password is too weak. Use at least 6 characters.';
          break;
        case 'auth/user-not-found':
          errorMessage = '❌ No account found with this email.\n\nPlease sign up first!';
          setAuthTab('signup');
          break;
        case 'auth/wrong-password':
          errorMessage = '❌ Incorrect password. Please try again.';
          break;
        case 'auth/too-many-requests':
          errorMessage = '❌ Too many failed attempts.\n\nPlease try again later or reset your password.';
          break;
        case 'auth/network-request-failed':
          errorMessage = '❌ Network error. Please check your internet connection.';
          break;
        default:
          errorMessage = error.message || 'Authentication failed';
      }
      setAuthError(errorMessage);
      alert(errorMessage);
    }
  };

  const handleSocialSignIn = async (provider) => {
    try {
      setAuthError('');
      if (provider === 'google') {
        await signInWithGoogle();
      } else if (provider === 'facebook') {
        await signInWithFacebook();
      }
      alert('✅ Signed in successfully!');
      onClose();
    } catch (error) {
      setAuthError(`Failed to sign in with ${provider}`);
    }
  };

  return (
    <div data-testid="auth-modal" className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="rock-background gig-card border-2 border-electric max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="concert-heading text-3xl text-electric">
            {authTab === 'signup' ? '🎸 JOIN US' : '🎤 SIGN IN'}
          </h2>
          <button 
            onClick={onClose} 
            className="text-white hover:text-red-400 p-2 touch-target"
          >
            <X size={32} />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => setAuthTab('signin')}
            className={`btn ${authTab === 'signin' ? 'btn-electric' : 'btn-ghost'}`}
          >
            Sign In
          </button>
          <button
            onClick={() => setAuthTab('signup')}
            className={`btn ${authTab === 'signup' ? 'btn-electric' : 'btn-ghost'}`}
          >
            Sign Up
          </button>
        </div>

        <div className="space-y-4">
          {/* Google Sign In */}
          <button
            onClick={() => handleSocialSignIn('google')}
            className="w-full px-6 py-4 bg-white hover:bg-gray-100 rounded-lg font-bold flex items-center justify-center gap-2 border-2 border-gray-400 transition-all hover:scale-[1.02]"
            style={{ color: '#000000' }}
          >
            <span style={{ color: '#000000' }}>🔐</span>
            <span style={{ color: '#000000', fontWeight: 'bold' }}>Continue with Google</span>
          </button>

          {/* Facebook Sign In */}
          <button
            onClick={() => handleSocialSignIn('facebook')}
            className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold flex items-center justify-center gap-2"
          >
            <span>📘</span>
            <span>Continue with Facebook</span>
          </button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-electric/30"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-dark text-electric font-bold uppercase tracking-wider">
                Or use email
              </span>
            </div>
          </div>

          {/* Error */}
          {authError && (
            <div className="bg-red-500/20 border border-red-400 rounded-lg p-4 text-red-200 text-sm">
              <p className="font-bold mb-1">⚠️ Error</p>
              <p>{authError}</p>
            </div>
          )}

          {/* Email */}
          <input
            data-testid="email-input"
            type="email"
            placeholder="Email"
            value={authEmail}
            onChange={(e) => setAuthEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
          />

          {/* Password */}
          <input
            data-testid="password-input"
            type="password"
            placeholder="Password"
            value={authPassword}
            onChange={(e) => setAuthPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleEmailAuth()}
            className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
          />

          {/* Forgot Password */}
          <div className="text-right">
            <button
              data-testid="forgot-password-link"
              type="button"
              onClick={onForgotPassword}
              className="text-electric hover:text-cyan-300 text-sm font-semibold transition-colors"
            >
              Forgot Password?
            </button>
          </div>

          {/* Submit */}
          <button
            data-testid="login-btn"
            onClick={handleEmailAuth}
            className="btn btn-neon w-full text-lg"
          >
            {authTab === 'signup' ? (
              <><span>⚡</span><span>Create Account</span></>
            ) : (
              <><span>⚡</span><span>Sign In</span></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
